import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faShieldAlt, faClock, faExclamationTriangle,
  faTimes, faArrowRight, faStar, faBuilding, faHome,
  faIdCard, faBolt, faHandshake, faChartLine,
} from "@fortawesome/free-solid-svg-icons";
import { useLanguage } from "../../../i18n/LanguageContext";
import "./KycPromptModal.css";

// ── Per-role content ──────────────────────────────────────────────────────────
const OWNER_CONTENT = {
  badgeLabel: "Property Owner",
  headline: "Unlock Your Full Owner Potential",
  subtext:
    "Verified owners earn more trust, get 3× more inquiries, and can receive digital rent payments — all with one quick verification.",
  benefits: [
    { icon: faStar,       iconClass: "",      text: <><strong>Verified badge</strong> on every listing — renters instantly notice and prefer you</> },
    { icon: faChartLine,  iconClass: "",      text: <><strong>Higher search ranking</strong> — verified listings appear above unverified ones</> },
    { icon: faHandshake,  iconClass: "green", text: <><strong>3× more rental inquiries</strong> — renters trust verified owners more</> },
    { icon: faBolt,       iconClass: "",      text: <><strong>Receive rent payments digitally</strong> through GharBhada — requires KYC</> },
    { icon: faShieldAlt,  iconClass: "green", text: <><strong>Legal protection</strong> in disputes — your identity is on official record</> },
    { icon: faIdCard,     iconClass: "",      text: <><strong>One-time process</strong> — verified permanently, no annual renewal</> },
  ],
  proof: [
    { num: "3×",   label: "More Inquiries" },
    { num: "82%",  label: "Owner Approval" },
    { num: "1–2",  label: "Days to Review" },
  ],
  ctaLabel: "Verify My Identity Now",
  laterLabel: "I'll verify later (not recommended — you're missing inquiries)",
};

const RENTER_CONTENT = {
  badgeLabel: "Renter",
  headline: "Get Ahead of Every Other Applicant",
  subtext:
    "Verified renters get approved twice as fast and are preferred by owners. Take 5 minutes now — be the applicant owners say yes to.",
  benefits: [
    { icon: faStar,       iconClass: "",      text: <><strong>KYC badge on your profile</strong> — stand out from hundreds of applications</> },
    { icon: faBolt,       iconClass: "",      text: <><strong>2× faster approvals</strong> — owners shortlist verified renters first</> },
    { icon: faHome,       iconClass: "green", text: <><strong>Apply to premium listings</strong> — some verified properties require KYC renters</> },
    { icon: faHandshake,  iconClass: "green", text: <><strong>Builds trust before you even message</strong> — owners respond faster to verified renters</> },
    { icon: faShieldAlt,  iconClass: "",      text: <><strong>Identity protected in disputes</strong> — KYC works both ways</> },
    { icon: faIdCard,     iconClass: "",      text: <><strong>One-time process</strong> — verified forever on GharBhada</> },
  ],
  proof: [
    { num: "2×",   label: "Faster Approval" },
    { num: "91%",  label: "Owner Preference" },
    { num: "5 min",label: "To Complete" },
  ],
  ctaLabel: "Verify My Identity Now",
  laterLabel: "Remind me later (I don't mind slower approvals)",
};

// ── Rejected / resubmit overlay message ──────────────────────────────────────
const REJECTION_CONTENT = {
  owner: {
    headline: "Action Required: Resubmit Your KYC",
    subtext:
      "Your previous submission was not accepted. Update your documents and resubmit to restore full access to your owner features.",
  },
  renter: {
    headline: "Action Required: Resubmit Your KYC",
    subtext:
      "Your previous submission was not accepted. Please resubmit with clear, valid documents to apply for rentals again.",
  },
};

// ─────────────────────────────────────────────────────────────────────────────
export default function KycPromptModal({ kycStatus, role }) {
  const navigate   = useNavigate();
  const { t } = useLanguage();
  const sessionKey = `kyc_modal_dismissed_${role}`;
  const [visible, setVisible] = useState(false);

  const tr = (key, fallback) => {
    const value = t(key);
    return value === key ? fallback : value;
  };

  useEffect(() => {
    if (
      kycStatus === "not_submitted" ||
      kycStatus === "rejected" ||
      kycStatus === "resubmit"
    ) {
      // Show again on every new browser session (not persisted in localStorage)
      const dismissed = sessionStorage.getItem(sessionKey);
      if (!dismissed) setVisible(true);
    }
  }, [kycStatus, sessionKey]);

  const dismiss = () => {
    sessionStorage.setItem(sessionKey, "1");
    setVisible(false);
  };

  const isOwner    = role === "owner";
  const translatedOwnerBenefits = t("kyc.modal.ownerBenefits");
  const translatedRenterBenefits = t("kyc.modal.renterBenefits");
  const ownerBenefitTexts = Array.isArray(translatedOwnerBenefits)
    ? translatedOwnerBenefits
    : OWNER_CONTENT.benefits.map((b) => (typeof b.text === "string" ? b.text : ""));
  const renterBenefitTexts = Array.isArray(translatedRenterBenefits)
    ? translatedRenterBenefits
    : RENTER_CONTENT.benefits.map((b) => (typeof b.text === "string" ? b.text : ""));

  const content = {
    ...(isOwner ? OWNER_CONTENT : RENTER_CONTENT),
    badgeLabel: isOwner
      ? tr("kyc.modal.ownerBadge", OWNER_CONTENT.badgeLabel)
      : tr("kyc.modal.renterBadge", RENTER_CONTENT.badgeLabel),
    headline: isOwner
      ? tr("kyc.modal.ownerHeadline", OWNER_CONTENT.headline)
      : tr("kyc.modal.renterHeadline", RENTER_CONTENT.headline),
    subtext: isOwner
      ? tr("kyc.modal.ownerSubtext", OWNER_CONTENT.subtext)
      : tr("kyc.modal.renterSubtext", RENTER_CONTENT.subtext),
    benefits: (isOwner ? OWNER_CONTENT.benefits : RENTER_CONTENT.benefits).map((b, i) => ({
      ...b,
      text: isOwner ? ownerBenefitTexts[i] || b.text : renterBenefitTexts[i] || b.text,
    })),
    ctaLabel: tr("kyc.modal.verifyNow", isOwner ? OWNER_CONTENT.ctaLabel : RENTER_CONTENT.ctaLabel),
    laterLabel: isOwner
      ? tr("kyc.modal.laterOwner", OWNER_CONTENT.laterLabel)
      : tr("kyc.modal.laterRenter", RENTER_CONTENT.laterLabel),
    proof: isOwner
      ? [
          { num: "3x", label: tr("kyc.modal.ownerProof1", OWNER_CONTENT.proof[0].label) },
          { num: "82%", label: tr("kyc.modal.ownerProof2", OWNER_CONTENT.proof[1].label) },
          { num: "1-2", label: tr("kyc.modal.ownerProof3", OWNER_CONTENT.proof[2].label) },
        ]
      : [
          { num: "2x", label: tr("kyc.modal.renterProof1", RENTER_CONTENT.proof[0].label) },
          { num: "91%", label: tr("kyc.modal.renterProof2", RENTER_CONTENT.proof[1].label) },
          { num: "5 min", label: tr("kyc.modal.renterProof3", RENTER_CONTENT.proof[2].label) },
        ],
  };
  const isRejected = kycStatus === "rejected" || kycStatus === "resubmit";

  const rejectionHeadline = tr("kyc.modal.actionRequiredTitle", REJECTION_CONTENT[role].headline);
  const rejectionSubtext = isOwner
    ? tr("kyc.modal.actionRequiredOwnerSubtext", REJECTION_CONTENT.owner.subtext)
    : tr("kyc.modal.actionRequiredRenterSubtext", REJECTION_CONTENT.renter.subtext);

  // ── Pending: sticky full-width strip, no dismiss ──────────────────────────
  if (kycStatus === "pending") {
    return (
      <div className="kyc-pending-strip">
        <FontAwesomeIcon icon={faClock} />
        <span className="pending-text">
          <strong>{tr("kyc.modal.pendingStripTitle", "KYC Under Review")}</strong> - {tr("kyc.modal.pendingStripMsg", "Your identity documents are being reviewed. We'll notify you once verified (usually 7–14 days). You'll gain full access immediately after approval.")}
        </span>
      </div>
    );
  }

  // ── Not modal-worthy or already approved ─────────────────────────────────
  if (!visible || kycStatus === "approved" || !kycStatus) return null;

  // ── Rejection strip that persists even after modal is dismissed ───────────
  const rejectedStripVisible = isRejected && !visible;

  return (
    <>
      {/* Persistent inline strip shown even if overlay was dismissed (rejected case) */}
      {rejectedStripVisible && (
        <div className="kyc-rejection-strip">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          <span>
            <strong>{kycStatus === "resubmit" ? tr("kyc.modal.rejectStripResubmitTitle", "KYC Resubmission Required") : tr("kyc.modal.rejectStripRejectedTitle", "KYC Rejected")}</strong> - {tr("kyc.modal.rejectStripMsg", "You must fix and resubmit your documents to use GharBhada fully.")}
          </span>
          <button onClick={() => navigate("/kyc-verification")}>
            {tr("kyc.modal.resubmitNow", "Resubmit Now")}
          </button>
        </div>
      )}

      {/* ── Full-screen modal overlay ─────────────────────────────────────── */}
      <div className="kyc-modal-overlay">
        <div className="kyc-modal-card">

          {/* Dismiss button — only session-level */}
          <button
            className="kyc-modal-close"
            onClick={dismiss}
            title="Dismiss — will show again next session"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>

          {/* Header */}
          <div className="kyc-modal-header">
            <div className={`kyc-modal-role-badge`}>
              <FontAwesomeIcon icon={isOwner ? faBuilding : faHome} />
              {content.badgeLabel}
            </div>

            <div className={`kyc-modal-icon-wrap ${isRejected ? "red" : isOwner ? "indigo" : "green"}`}>
              <FontAwesomeIcon icon={isRejected ? faExclamationTriangle : faShieldAlt} />
            </div>

            <h2>
              {isRejected
                ? rejectionHeadline
                : content.headline}
            </h2>
            <p>
              {isRejected
                ? rejectionSubtext
                : content.subtext}
            </p>
          </div>

          {/* Social proof numbers */}
          {!isRejected && (
            <div className="kyc-social-proof">
              {content.proof.map((p, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <div className="kyc-proof-divider" />}
                  <div className="kyc-proof-stat">
                    <span className="stat-num">{p.num}</span>
                    <span className="stat-label">{p.label}</span>
                  </div>
                </React.Fragment>
              ))}
            </div>
          )}

          {/* Benefits list */}
          <div className="kyc-modal-benefits">
            {content.benefits.map((b, i) => (
              <div key={i} className="kyc-benefit-row">
                <span className={`kyc-benefit-icon ${b.iconClass || ""}`}>
                  <FontAwesomeIcon icon={b.icon} />
                </span>
                <span>{b.text}</span>
              </div>
            ))}
          </div>

          {/* Primary CTA */}
          <button
            className={`kyc-modal-cta${isRejected ? " urgent" : ""}`}
            onClick={() => navigate("/kyc-verification")}
          >
            <FontAwesomeIcon icon={faShieldAlt} />
            {isRejected ? tr("kyc.modal.fixAndResubmit", "Fix & Resubmit Documents") : content.ctaLabel}
            <FontAwesomeIcon icon={faArrowRight} />
          </button>

          {/* Session-dismiss link */}
          <button className="kyc-modal-later" onClick={dismiss}>
            {content.laterLabel}
          </button>

        </div>
      </div>
    </>
  );
}
